// The Last Supper's choice, the three ending sequences, credits and REMEMBER? (docs/06 §7).
// The sim has already decided everything; these are scripted DOM shots that advance on a timer
// (scaled by the speed setting) or a click. UNPLUGGED has two drag interactions.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { B } from '../../content/balance';
import { t } from '../../i18n/t';
import { audio } from '../../audio/audio';
import { controller } from '../../render';
import { endingChoices } from '../../sim/ending';
import type { EndingId } from '../../sim/types';
import { getGame, setUI, useGame, useUI } from '../../state/store';
import { Btn, Icon } from '../common';

const ORDER: EndingId[] = ['sated', 'maw', 'unplugged'];
const LOCKED: Partial<Record<EndingId, string>> = { maw: 'ui.ending.lockedMaw', unplugged: 'ui.ending.lockedUnplugged' };

export function EndingChoice() {
  const stage = useUI((u) => u.stage);
  const cinematic = useUI((u) => u.cinematic);
  const busy = useUI((u) => u.busy);
  const game = useGame((g) => g);
  if (stage !== 'ending' || cinematic || game.run?.phase !== 'ending') return null;
  const open = endingChoices(game.meta);
  return (
    <div className="overlay interactive" style={{ flexDirection: 'column', gap: 10 }} data-testid="ending-choice">
      <div className="h2" style={{ color: '#f9c22b' }}>
        {t('ui.ending.title')}
      </div>
      <div className="h">{t('ui.ending.prompt')}</div>
      <div className="col" style={{ gap: 6, alignItems: 'stretch', width: 260 }}>
        {ORDER.map((id) =>
          open.includes(id) ? (
            <Btn key={id} kind={id === 'maw' ? 'danger' : id === 'unplugged' ? 'primary' : 'gold'} disabled={busy} onClick={() => controller.act({ t: 'CHOOSE_ENDING', ending: id })} testId={`ending-${id}`}>
              {t(`ui.ending.${id}`)}
            </Btn>
          ) : (
            <div key={id} className="dim" style={{ textAlign: 'center', padding: 4 }}>
              {t(LOCKED[id]!)}
            </div>
          ),
        )}
      </div>
    </div>
  );
}

export function Remember() {
  const stage = useUI((u) => u.stage);
  const cinematic = useUI((u) => u.cinematic);
  const busy = useUI((u) => u.busy);
  const game = useGame((g) => g);
  if (stage !== 'ending' || cinematic || game.run?.phase !== 'remember') return null;
  const best = game.meta.lifetime.bestEndless;
  return (
    <div className="overlay interactive" style={{ flexDirection: 'column', gap: 10 }} data-testid="remember">
      <div className="h2" style={{ color: '#a884f3' }}>
        {t('ui.remember.title')}
      </div>
      <Btn kind="danger" disabled={busy} onClick={() => controller.act({ t: 'REMEMBER', choice: 'endless' })} testId="remember-endless">
        {t('ui.remember.endless')}
      </Btn>
      {best > 0 && <div className="dim">{t('ui.endless.best', { n: best })}</div>}
      <Btn disabled={busy} onClick={() => controller.act({ t: 'REMEMBER', choice: 'forget' })} testId="remember-forget">
        {t('ui.remember.forget')}
      </Btn>
      <div className="dim" style={{ width: 300, textAlign: 'center', lineHeight: '11px' }}>
        {t('ui.remember.forgetHint')}
      </div>
    </div>
  );
}

// ── Cinematics ─────────────────────────────────────────────────────────

interface Shot {
  ms: number;
  /** Interactive: waits for `next()` instead of a timer or a click. */
  wait?: boolean;
  enter?: () => void;
  bg?: string;
  body: (next: () => void) => ReactNode;
}

const reduce = () => getGame().meta.settings.reduceFlashing;
const lines = (keys: string[], cls = 'maw-line') =>
  keys.map((k, i) => (
    <div key={k} className={`${cls} fade-in`} style={{ animationDelay: `${i * 900}ms` }}>
      {t(k)}
    </div>
  ));

function Credits({ ending, led }: { ending: EndingId; led?: boolean }) {
  const count = getGame().meta.lifetime.endings.length;
  return (
    <div className={`credits ${led ? 'credits-led' : ''}`}>
      <div className="credits-roll">
        <div className="h2">{t('ui.ending.credits')}</div>
        <div>{t('ui.ending.creditsLine')}</div>
        <div className="gap" />
        <div className="h">{t('ui.ending.creditsFor', { name: t(`ui.ending.name.${ending}`) })}</div>
        <div className="dim">{t('ui.ending.unlocked', { n: count, name: t(`ui.ending.name.${ending}`) })}</div>
        <div className="gap" />
        <div>{t('ui.ending.creditsMade')}</div>
        <div>{t('ui.ending.creditsRegular')}</div>
        <div className="gap" />
        <div className="h">{t('ui.ending.thanks')}</div>
      </div>
    </div>
  );
}

const PIECES = B.PLUG_PARTS.map((id) => `item_${id}`);

/** Drag the five pieces onto their outlines. */
function Assemble({ done }: { done: () => void }) {
  const [placed, setPlaced] = useState<boolean[]>(() => PIECES.map(() => false));
  const [drag, setDrag] = useState<{ i: number; x: number; y: number } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const slotX = (i: number) => 150 + i * 70;
  const SLOT_Y = 80;
  const home = (i: number) => ({ x: 126 + i * 80, y: 200 });
  const local = (e: React.PointerEvent) => {
    const r = box.current!.getBoundingClientRect();
    const k = 640 / r.width;
    return { x: (e.clientX - r.left) * k, y: (e.clientY - r.top) * k };
  };
  useEffect(() => {
    if (placed.every(Boolean)) {
      audio.play('relic');
      const id = setTimeout(done, 500);
      return () => clearTimeout(id);
    }
  }, [placed, done]);
  return (
    <div
      ref={box}
      className="scene-abs"
      onPointerMove={(e) => drag && setDrag({ ...drag, ...local(e) })}
      onPointerUp={() => {
        if (!drag) return;
        const near = Math.abs(drag.x - (slotX(drag.i) + 26)) < 30 && Math.abs(drag.y - (SLOT_Y + 26)) < 30;
        if (near) {
          audio.play('stamp', { pitch: drag.i * 2 });
          setPlaced((p) => p.map((v, k) => v || k === drag.i));
        }
        setDrag(null);
      }}
    >
      <div className="cine-text" style={{ top: 30 }}>
        {t('ui.ending.assemble')}
      </div>
      {PIECES.map((id, i) => (
        <div key={`slot${i}`} className="plug-slot" style={{ left: slotX(i), top: SLOT_Y }}>
          {placed[i] && <Icon id={id} scale={2} />}
        </div>
      ))}
      {PIECES.map((id, i) => {
        if (placed[i]) return null;
        const p = drag?.i === i ? { x: drag.x - 24, y: drag.y - 24 } : home(i);
        return (
          <div
            key={id}
            className="plug-piece"
            style={{ left: p.x, top: p.y }}
            onPointerDown={(e) => {
              (e.currentTarget.parentElement as HTMLElement).setPointerCapture?.(e.pointerId);
              audio.play('click');
              setDrag({ i, ...local(e) });
            }}
          >
            <Icon id={id} scale={2} />
          </div>
        );
      })}
    </div>
  );
}

/** Drag the assembled plug out of the wall. */
function PullPlug({ done }: { done: () => void }) {
  const [x, setX] = useState(0);
  const start = useRef<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const pulled = x > 110;
  const k = () => 640 / (box.current?.getBoundingClientRect().width || 640);
  useEffect(() => {
    if (!pulled) return;
    audio.play('static');
    audio.play('rumble', { pitch: -8 });
    audio.music.tapeStop();
    const id = setTimeout(done, 700);
    return () => clearTimeout(id);
  }, [pulled, done]);
  return (
    <div
      ref={box}
      className="scene-abs"
      onPointerMove={(e) => start.current !== null && !pulled && setX(Math.max(0, (e.clientX - start.current) * k()))}
      onPointerUp={() => {
        start.current = null;
        if (!pulled) setX(0);
      }}
    >
      <div className="cine-text" style={{ top: 30 }}>
        {t('ui.ending.pull')}
      </div>
      <div className="socket" style={{ left: 250, top: 120 }} />
      <div className="cable" style={{ left: 0, top: 150, width: 262 + x }} />
      <div
        className="plug-assembled"
        style={{ left: 262 + x, top: 128, cursor: pulled ? 'default' : 'grab' }}
        onPointerDown={(e) => {
          (e.currentTarget.parentElement as HTMLElement).setPointerCapture?.(e.pointerId);
          start.current = e.clientX - x / k();
        }}
      >
        {PIECES.map((id) => (
          <Icon key={id} id={id} />
        ))}
      </div>
    </div>
  );
}

function shotsFor(ending: EndingId, first: boolean): Shot[] {
  const cosmetic = first && ending === 'maw';
  if (ending === 'sated')
    return [
      { ms: 2200, enter: () => (audio.play('ratchet'), audio.play('clack')), body: () => <div className="cine-text">{t('ui.ending.lastPull')}</div> },
      {
        ms: 6000,
        enter: () => audio.play('swell'),
        body: () => (
          <div className="col" style={{ gap: 8, alignItems: 'center' }}>
            <div className="grins">
              {Array.from({ length: 25 }, (_, i) => (
                <div key={i} className="fade-in" style={{ animationDelay: `${i * 60}ms` }}>
                  <Icon id="sym_grin" />
                </div>
              ))}
            </div>
            {lines(['maw.sated.1', 'maw.sated.2', 'maw.sated.3'])}
          </div>
        ),
      },
      {
        ms: 3200,
        enter: () => {
          audio.play('door');
          audio.play('rain');
        },
        bg: 'var(--ink)',
        body: () => (
          <div className="col" style={{ gap: 10, alignItems: 'center' }}>
            <Icon id="window_rain" scale={2} />
            <div className="cine-text">{t('ui.ending.door')}</div>
          </div>
        ),
      },
      {
        ms: 3800,
        enter: () => audio.play('sign'),
        body: () => (
          <div className="sign">
            <span className={reduce() ? 'sign-old fade-out' : 'sign-old flicker-out'}>{t('ui.ending.sign')}</span>
            <span className={reduce() ? 'sign-new fade-in-late' : 'sign-new flicker-in'}>{t('ui.ending.signMaw')}</span>
          </div>
        ),
      },
      { ms: 9000, body: () => <Credits ending="sated" /> },
      {
        ms: 3500,
        enter: () => audio.play('coin'),
        body: () => (
          <div className="col" style={{ gap: 10, alignItems: 'center' }}>
            <div className="roll-in">
              <Icon id="coin" scale={2} />
            </div>
            <div className="maw-line fade-in" style={{ animationDelay: '900ms' }}>
              {t('ui.ending.seeYou')}
            </div>
          </div>
        ),
      },
    ];
  if (ending === 'maw')
    return [
      { ms: 2600, enter: () => audio.play('gorge'), bg: 'var(--crimson)', body: () => <div className="cine-text">{t('ui.ending.inside')}</div> },
      {
        ms: 3400,
        body: () => (
          <div className="glass">
            <div className="row" style={{ gap: 24, alignItems: 'flex-end' }}>
              <Icon id="cabinet_hollow" />
              <Icon id="payphone" />
              <Icon id="clock" />
              <Icon id="cabinet_hollow" />
            </div>
            <div className="cine-text">{t('ui.ending.glass')}</div>
          </div>
        ),
      },
      {
        ms: 3400,
        enter: () => audio.play('static', { volume: 0.3 }),
        body: () => (
          <div className="glass">
            <div className="guest walk-in" />
            <div className="cine-text">{t('ui.ending.guest')}</div>
          </div>
        ),
      },
      {
        ms: 4200,
        enter: () => audio.play('laugh', { pitch: -4 }),
        body: () => (
          <div className="led-panel">
            <div className="led-text">{t('maw.becomeMaw.1')}</div>
            <div className={`led-text ${reduce() ? 'fade-in-late' : 'flicker-in'}`}>{t('maw.becomeMaw.2')}</div>
          </div>
        ),
      },
      { ms: 9000, body: () => <Credits ending="maw" led /> },
      ...(cosmetic ? [{ ms: 2600, enter: () => audio.play('relic'), body: () => <div className="cine-text" style={{ color: '#f04f78' }}>{t('ui.ending.cosmetic')}</div> }] : []),
    ];
  return [
    {
      ms: 2800,
      enter: () => audio.play('rumble', { pitch: -4, volume: 0.6 }),
      body: () => (
        <div className="scene-abs wall">
          <div className="cable" style={{ left: 0, top: 150, width: 262 }} />
          <div className="socket" style={{ left: 250, top: 120 }} />
          <div className="cine-text" style={{ top: 240 }}>
            {t('ui.ending.behind')}
          </div>
        </div>
      ),
    },
    { ms: 0, wait: true, body: (next) => <Assemble done={next} /> },
    { ms: 0, wait: true, body: (next) => <PullPlug done={next} /> },
    { ms: 4800, body: () => <div className="col fading">{lines(['maw.unplugged.1', 'maw.unplugged.2', 'maw.unplugged.3'], 'maw-line maw-dying')}</div> },
    { ms: 2400, bg: 'var(--ink)', body: () => <div className="cine-text dim">{t('ui.ending.lightsDie')}</div> },
    {
      ms: 4000,
      bg: 'var(--ink)',
      enter: () => audio.play('phone'),
      body: () => (
        <div className="col" style={{ gap: 10, alignItems: 'center' }}>
          <div className="dim">{t('ui.ending.ringing')}</div>
          <div className="dolores fade-in" style={{ animationDelay: '1200ms' }}>
            {t('dolores.farewell.1')}
          </div>
        </div>
      ),
    },
    {
      ms: 4000,
      bg: 'var(--cream)',
      enter: () => {
        audio.music.setDecay(0);
        audio.music.start();
      },
      body: () => (
        <div className="col dawn" style={{ gap: 10, alignItems: 'center' }}>
          <Icon id="clock" scale={2} />
          <div className="h">{t('ui.ending.dawn')}</div>
        </div>
      ),
    },
    {
      ms: 9000,
      bg: 'var(--cream)',
      body: () => (
        <div className="dawn">
          <Credits ending="unplugged" />
        </div>
      ),
    },
  ];
}

export function Cinematic() {
  const cinematic = useUI((u) => u.cinematic);
  const speed = useGame((g) => Math.min(2, Math.max(1, g.meta.settings.speed)));
  const [idx, setIdx] = useState(0);
  const shots = useMemo(() => (cinematic ? shotsFor(cinematic.ending, cinematic.first) : []), [cinematic]);
  useEffect(() => setIdx(0), [cinematic]);
  const shot = shots[idx];
  const finish = () => setUI({ cinematic: null });
  const next = () => (idx + 1 >= shots.length ? finish() : setIdx(idx + 1));
  useEffect(() => {
    if (!shot) return;
    shot.enter?.();
    if (shot.wait) return;
    const id = setTimeout(next, shot.ms / speed);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shot]);
  if (!cinematic || !shot) return null;
  return (
    <div
      className="cinema interactive"
      style={{ background: shot.bg ?? 'var(--ink)', ['--shot-ms' as string]: `${shot.ms / speed}ms` }}
      onClick={() => !shot.wait && next()}
      data-testid="cinematic"
    >
      <div key={idx} className="shot" style={{ animationDuration: `${400 / speed}ms` }}>
        {shot.body(next)}
      </div>
      <div className="cine-skip" onClick={(e) => e.stopPropagation()}>
        <Btn kind="ghost" onClick={finish}>
          {t('ui.ending.skip')}
        </Btn>
      </div>
      {!shot.wait && <div className="cine-hint dim">{t('ui.ending.continue')}</div>}
    </div>
  );
}

export function EndingScreens() {
  return (
    <>
      <EndingChoice />
      <Remember />
      <Cinematic />
    </>
  );
}

